import { useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  FormLabel,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  DECISION_META,
  HV_DECISIONS,
  PIPELINE_LIST_LABELS,
  PIPELINE_LISTS,
  type HouseholdSummary,
  type HvDecision,
  type PipelineList,
} from '@hv/shared';
import { useHouseholds } from '../api/hooks';
import { DecisionPill, Dot, RecTypeTag, ScoreBar, SignalChips } from '../components/badges';
import { describeError } from '../components/Notify';
import { BRAND, DECISION_COLORS } from '../theme';
import { useUrlState } from '../urlState';

const BANDS = [
  { value: 'all', label: 'All' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
  { value: 'unscored', label: 'Not yet scored' },
] as const;

function parseDecisions(v: string | null): HvDecision[] {
  if (v === null) return [...HV_DECISIONS];
  if (v === 'none') return [];
  return v.split(',').filter((d): d is HvDecision => (HV_DECISIONS as readonly string[]).includes(d));
}

export function applicantsText(h: HouseholdSummary) {
  return h.applicants.map((a) => `${a.firstName}${a.gradeApplying ? ` (Gr ${a.gradeApplying})` : ''}`).join(', ');
}

const columns: GridColDef<HouseholdSummary>[] = [
  {
    field: 'familyLastName',
    headerName: 'Family',
    flex: 1.2,
    minWidth: 180,
    renderCell: ({ row }) => (
      <Box sx={{ py: 1, lineHeight: 1.3 }}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{row.familyLastName}</Typography>
          {row.openDuplicates > 0 && (
            <Tooltip title="Possible duplicate needs review">
              <WarningAmberIcon fontSize="small" color="warning" aria-label="Possible duplicate needs review" />
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" gap={0.5} flexWrap="wrap" mt={0.5}>
          {row.origin === 'manual_prospect' && <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: '#12707c', bgcolor: '#e6f4f6', borderRadius: 1, px: 0.6 }}>prospect</Box>}
          {row.recommendationTypes.map((t) => (
            <RecTypeTag key={t} type={t} />
          ))}
        </Stack>
      </Box>
    ),
  },
  { field: 'applicants', headerName: 'Applicant(s) / Grade', flex: 1.2, minWidth: 170, valueGetter: (_v, row) => applicantsText(row) },
  { field: 'recommenders', headerName: 'Recommender', flex: 1, minWidth: 140, valueGetter: (_v, row) => row.recommenders.join(', ') || '—' },
  {
    field: 'effectiveScore',
    headerName: 'Impact Potential',
    width: 170,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <ScoreBar score={row.effectiveScore} band={row.band} overridden={row.overridden} status={row.assessmentStatus} aiScore={row.aiScore} />
      </Box>
    ),
  },
  {
    field: 'signals',
    headerName: 'Impact Signals',
    flex: 1.3,
    minWidth: 180,
    sortable: false,
    renderCell: ({ row }) => (
      <Box sx={{ py: 1 }}>
        <SignalChips signals={row.signals} />
      </Box>
    ),
  },
  {
    field: 'decisions',
    headerName: 'Decision',
    flex: 1,
    minWidth: 160,
    sortComparator: (a: HvDecision[], b: HvDecision[]) => DECISION_META[a[0]!].label.localeCompare(DECISION_META[b[0]!].label),
    renderCell: ({ row }) => (
      <Stack gap={0.5} sx={{ py: 1 }}>
        {row.applicants.length > 1
          ? row.applicants.map((a) => <DecisionPill key={a.id} decision={a.hvDecision} label={a.firstName} />)
          : row.decisions.map((d) => <DecisionPill key={d} decision={d} />)}
      </Stack>
    ),
  },
];

export function PipelinePage() {
  const { params, set } = useUrlState();
  const list = (PIPELINE_LISTS as readonly string[]).includes(params.get('list') ?? '') ? (params.get('list') as PipelineList) : 'all';
  const band = params.get('band') ?? 'all';
  const decisions = parseDecisions(params.get('decisions'));
  const allDecisions = decisions.length === HV_DECISIONS.length;
  const query = useHouseholds({
    cycleId: params.get('cycle') ?? undefined,
    list,
    band,
    q: params.get('q') ?? undefined,
    decisions: allDecisions || !decisions.length ? undefined : decisions,
  });

  useEffect(() => {
    if (query.error) console.error('Loading households failed', query.error);
  }, [query.error]);

  const rows = useMemo(() => (decisions.length ? (query.data?.items ?? []) : []), [query.data, decisions.length]);
  const high = rows.filter((r) => r.band === 'high').length;

  const toggleDecision = (d: HvDecision) => {
    const next = decisions.includes(d) ? decisions.filter((x) => x !== d) : [...decisions, d];
    set({ decisions: next.length === HV_DECISIONS.length ? null : next.length ? next.join(',') : 'none' });
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '260px 1fr' }, gap: 2, p: 2 }}>
      <Box component="aside" aria-label="Filters">
        <Paper sx={{ p: 1, mb: 2 }}>
          <List dense aria-label="Lists" sx={{ display: { xs: 'flex', md: 'block' }, overflowX: 'auto' }}>
            {PIPELINE_LISTS.map((l) => (
              <ListItemButton
                key={l}
                selected={l === list}
                onClick={() => set({ list: l === 'all' ? null : l })}
                sx={{ borderRadius: 1, flexShrink: 0 }}
                data-list={l}
              >
                <ListItemText primary={PIPELINE_LIST_LABELS[l]} slotProps={{ primary: { sx: { fontSize: 14, fontWeight: l === list ? 700 : 500 } } }} />
                <Box component="span" data-testid={`count-${l}`} sx={{ ml: 1, fontSize: 12, px: 1, borderRadius: 10, bgcolor: l === list ? '#cdd8ef' : '#eceff3', color: l === list ? BRAND.navy : BRAND.muted }}>
                  {query.data?.counts[l] ?? '…'}
                </Box>
              </ListItemButton>
            ))}
          </List>
        </Paper>
        <Paper sx={{ p: 2, mb: 2 }}>
          <FormLabel component="legend" sx={{ typography: 'h4', mb: 1 }}>
            Decision
          </FormLabel>
          <Stack>
            {HV_DECISIONS.map((d) => (
              <FormControlLabel
                key={d}
                control={<Checkbox size="small" checked={decisions.includes(d)} onChange={() => toggleDecision(d)} />}
                label={
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Dot color={DECISION_COLORS[DECISION_META[d].color].dot} />
                    <span style={{ fontSize: 14 }}>{DECISION_META[d].label}</span>
                  </Stack>
                }
              />
            ))}
          </Stack>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <FormLabel id="band-label" component="legend" sx={{ typography: 'h4', mb: 1 }}>
            Impact Potential
          </FormLabel>
          <RadioGroup aria-labelledby="band-label" value={band} onChange={(e) => set({ band: e.target.value === 'all' ? null : e.target.value })}>
            {BANDS.map((b) => (
              <FormControlLabel
                key={b.value}
                value={b.value}
                control={<Radio size="small" />}
                label={
                  <span style={{ fontSize: 14 }}>
                    {b.label}
                    {query.data && b.value === 'high' && ` (${query.data.thresholds.high}+)`}
                    {query.data && b.value === 'medium' && ` (${query.data.thresholds.medium}–${query.data.thresholds.high - 1})`}
                    {query.data && b.value === 'low' && ` (<${query.data.thresholds.medium})`}
                  </span>
                }
              />
            ))}
          </RadioGroup>
        </Paper>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
          <Typography variant="h2">{PIPELINE_LIST_LABELS[list]}</Typography>
          <Typography variant="body2" color="text.secondary" data-testid="pipeline-stats">
            {rows.length} shown · {high} high-visibility
          </Typography>
        </Stack>
        {query.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not load households: {describeError(query.error)}
          </Alert>
        )}
        <Paper data-testid="households-grid" component="section" aria-label="Households">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={query.isFetching && !query.data}
            getRowHeight={() => 'auto'}
            disableRowSelectionOnClick
            onRowClick={({ row }) => set({ h: row.id }, false)}
            onCellKeyDown={({ row }, e) => {
              if (e.key === 'Enter') set({ h: row.id }, false);
            }}
            initialState={{ sorting: { sortModel: [{ field: 'effectiveScore', sort: 'desc' }] }, pagination: { paginationModel: { pageSize: 50 } } }}
            pageSizeOptions={[25, 50, 100]}
            localeText={{ noRowsLabel: 'No households match the current filters.' }}
            sx={{ border: 0, minHeight: 300, '& .MuiDataGrid-row': { cursor: 'pointer' }, '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' } }}
            autoHeight
          />
        </Paper>
      </Box>
    </Box>
  );
}
